/**
 * ENG-8877 — tell apart the three ways an agent's platform auth can fail.
 *
 * WHY THIS EXISTS, AND WHY IT IS NOT THE FIX I FIRST PROPOSED
 * ----------------------------------------------------------
 * I filed ENG-8877 claiming that a session preserved through the 2026-08-14
 * outage (ENG-8869) was permanently stranded: healthy to the manager, 401 to
 * every MCP call, unable to refresh itself because `token_refresh` is an MCP
 * tool. That was wrong, and the fix I proposed — respawn the session to
 * re-mint — was wrong twice over. Recording it here because the wrong model is
 * the thing that will be re-derived by the next person reading a 401 in an
 * incident channel:
 *
 *   * Refresh is NOT a tool call. It happens inside the MCP child, with no
 *     agent involvement. `packages/mcp/src/index.ts:207` re-exchanges when the
 *     cached JWT is within 5 minutes of expiry, and `:268-272` zeroes the cache
 *     and retries once on a 401. A normal agent therefore self-heals on the
 *     first call after the tier recovers, and a respawn would be pure churn —
 *     the ENG-8344 cost (gordon: 23 respawns in 17 hours) that ENG-8877's own
 *     description argued against while proposing it.
 *
 *   * The one shape that IS stranded is not fixed by a respawn either. An
 *     `AGT_AGENT_SESSION_TOKEN` (impersonation) session never exchanges at all
 *     — `getToken()` returns it verbatim (`:198-205`) and the 401 retry is
 *     explicitly gated `&& !AGT_AGENT_SESSION_TOKEN` (`:268`). The value is
 *     baked into `.mcp.json` by `impersonate-mcp-rewrite.ts:203`, so a respawn
 *     re-reads the same dead token.
 *
 * So the defect worth fixing is not the recovery — it is that the host cannot
 * SEE any of this. `401 Invalid or expired token` appears nowhere under
 * `apps/cli`. `isSessionHealthy` (`persistent-session.ts:3014`) is
 * `tmux has-session` plus a pgrep-style zombie probe; it cannot observe auth.
 * A session whose every platform call 401s reports `decision=healthy` forever
 * (`manager-worker.ts` decision tail).
 *
 * That is ENG-8872's rule — never collapse three states into two — arriving in
 * the session layer. These are the three, and they have three different
 * remedies:
 *
 *   exchange-unavailable  our identity tier is down; the credential is fine.
 *                         Wait. This is what the whole fleet looked like on
 *                         2026-08-14 and it is NOT a credential problem.
 *   host-key-rejected     the exchange itself succeeded or was retried and the
 *                         API still says no. The host key is revoked or the
 *                         agent is unbound. A human has to re-bind it.
 *   session-token-expired an impersonation session's fixed token is dead. No
 *                         refresh path exists by design. Needs a fresh
 *                         `agt impersonate connect` — a respawn will not do it.
 *
 * DIRECTION OF THE DEFAULT. Unrecognised text is NOT an auth failure. Same
 * posture as the CLI connectivity probe (ENG-5463): the loud-and-wrong error
 * here is calling a healthy session auth-dead, because the remedies are a
 * restart or a page to a human. A missed signature leaves today's behaviour,
 * which is silence. So a signature has to opt IN.
 *
 * WHERE THE SIGNAL COMES FROM. The transcript, not the pane. Measured on this
 * host: a tool error's text reaches the JSONL `tool_result` block in full
 * (`{"is_error":true,...,"Slack error: invalid_arguments"}`), and does NOT
 * reach `pane.log` — the TUI collapses it to `Error: API Error`. A pane
 * scraper for this, which is where I started, would never have fired.
 */

import { closeSync, openSync, readSync, readdirSync, statSync } from 'node:fs';
import { join } from 'node:path';

import { sessionTranscriptDir } from './daily-session.js';

/** Which of the three failures a tool_result describes. */
export type PlatformAuthFailureKind =
  /** `/host/exchange` itself is unreachable or erroring. Our tier, not the credential. */
  | 'exchange-unavailable'
  /** A 401 that survived the automatic re-exchange. The host key is rejected. */
  | 'host-key-rejected'
  /** An impersonation session token expired. There is no refresh path by design. */
  | 'session-token-expired';

export interface PlatformAuthFailure {
  kind: PlatformAuthFailureKind;
  /**
   * True when the MCP child's own retry path will recover this without anyone
   * doing anything, once the underlying condition clears.
   *
   * This is the field that would have stopped ENG-8877 being filed wrong:
   * `exchange-unavailable` is self-recovering and looks identical, in a chat
   * message, to the two that are not.
   */
  selfRecovering: boolean;
  /** What a human or the manager should do. One line, operator-facing. */
  remedy: string;
}

/**
 * Signatures, each anchored on text a platform component actually emits.
 *
 * Order matters: the agent-session shape is checked first because its message
 * also contains `returned 401`, and it is the only one where a respawn is
 * actively useless. Getting that precedence backwards would report the one
 * genuinely stranded case as the one that heals itself.
 */
const SIGNATURES: ReadonlyArray<{
  re: RegExp;
  kind: PlatformAuthFailureKind;
  selfRecovering: boolean;
  remedy: string;
}> = [
  {
    // packages/mcp/src/index.ts:279-283, verbatim.
    re: /agent-session rejected \(expired or revoked\)/i,
    kind: 'session-token-expired',
    selfRecovering: false,
    remedy:
      'impersonation session token is dead and does not refresh — needs a fresh `agt impersonate connect`; a respawn will re-read the same token from .mcp.json',
  },
  {
    // packages/mcp/src/index.ts:216-219, thrown when the exchange fails and no
    // cached token exists. The tier is down; the credential was never tested.
    // The connection-refused variants are scoped to the exchange on purpose:
    // `ECONNREFUSED` on its own could be any tool's HTTP client, and grading
    // that as a platform-auth state would be a claim about the wrong thing.
    re: /Cannot authenticate: token exchange failed and no cached token available|Token exchange failed: 5\d\d|token exchange failed:[\s\S]{0,200}?(?:ECONNREFUSED|econnrefused|errno 111|connection refused|delayed connect error|upstream connect error|disconnect\/reset before headers|no healthy upstream|fetch failed|socket hang up)/i,
    kind: 'exchange-unavailable',
    selfRecovering: true,
    remedy:
      'our identity tier is unreachable, not the credential — the child re-exchanges on the next call once it recovers; do not restart and do not tell the customer to reconnect',
  },
  {
    // The host-JWT path after `:268-272` already forced one re-exchange. A 401
    // that survives that is a verdict on the key, not an expiry.
    re: /returned 401[\s\S]{0,200}?Invalid or expired token/i,
    kind: 'host-key-rejected',
    selfRecovering: false,
    remedy:
      'the host key was rejected after an automatic re-exchange — the agent is unbound or its key is revoked; needs a human to re-bind, not a restart',
  },
];

/**
 * Classify one tool_result's error text.
 *
 * @returns the failure, or `null` when nothing recognises the text — which is
 *          the overwhelmingly common case and must never be read as "fine".
 *          It means "not an auth failure we can name", nothing more.
 */
export function classifyPlatformAuthFailure(text: string | null | undefined): PlatformAuthFailure | null {
  if (!text) return null;
  for (const { re, kind, selfRecovering, remedy } of SIGNATURES) {
    if (re.test(text)) return { kind, selfRecovering, remedy };
  }
  return null;
}

/** One classified observation, with enough context to report it. */
export interface AuthFailureObservation {
  kind: PlatformAuthFailureKind;
  selfRecovering: boolean;
  remedy: string;
  /** Tool the failing call belonged to, when the transcript names it. */
  toolName: string | null;
}

/**
 * What the manager should say about this session's platform auth this tick.
 *
 *   ok        nothing recognised. NOT a claim that auth is verified — see the
 *             note on `classifyPlatformAuthFailure`.
 *   degraded  only self-recovering failures seen. Worth reporting, worth NOT
 *             acting on. This is the state the fleet was in on 2026-08-14, and
 *             the state that must never be escalated to a customer.
 *   dead      at least one failure that will not clear on its own.
 */
export type SessionAuthState = 'ok' | 'degraded' | 'dead';

export interface SessionAuthVerdict {
  state: SessionAuthState;
  /** The worst failure seen, or null when `state === 'ok'`. */
  worst: AuthFailureObservation | null;
  /** How many recognised auth failures were in the window. */
  count: number;
  /**
   * Short non-secret tag for the `[persistent-session-decision]` log line, so
   * a healthy-but-auth-dead session stops being indistinguishable from a
   * healthy one in the only place an operator looks.
   */
  detail: string;
}

/**
 * Reduce a window of observations to one verdict.
 *
 * `dead` beats `degraded` regardless of counts. A single non-recovering
 * failure is a fact about the credential; a hundred exchange timeouts are a
 * fact about our tier. Counting them against each other would let a noisy
 * outage bury the one observation that needs a human.
 */
/**
 * ENG-8877, after review — is this non-recovering observation more likely to be
 * collateral from a token-exchange outage than an independent verdict?
 *
 * THE HOLE THIS CLOSES, WHICH WAS MINE. When `/host/exchange` is down but the
 * MCP child still holds a stale cached JWT, `getToken()` logs the exchange
 * failure and returns the stale token anyway (packages/mcp/src/index.ts:224-232).
 * The API then 401s it, the forced re-exchange fails too, and the agent gets a
 * plain `returned 401 … Invalid or expired token` — textually IDENTICAL to a
 * genuinely revoked host key.
 *
 * My first cut graded that `host-key-rejected`, i.e. "a human must re-bind
 * this agent". During a tier outage that is the wrong tier for every affected
 * agent at once — which is ENG-8878's defect, reproduced inside ENG-8877's fix,
 * by me, on the same day I wrote the ticket complaining about it.
 *
 * The transcript cannot separate the two texts, but it does not have to: if
 * the SAME window also contains direct evidence that the exchange is failing,
 * the parsimonious reading of a 401 is collateral, not a second independent
 * fault. A revoked key still reports `dead` the moment the outage evidence
 * ages out of the window, which is minutes — a delay, against telling the
 * whole fleet to re-bind during an outage.
 *
 * `session-token-expired` is never demoted: it carries its own distinct
 * message, it is unambiguous, and it does not self-heal at any point.
 */
function isCollateralOf(o: AuthFailureObservation, outageInWindow: boolean): boolean {
  return outageInWindow && o.kind === 'host-key-rejected';
}

export function decideSessionAuthState(observations: readonly AuthFailureObservation[]): SessionAuthVerdict {
  if (observations.length === 0) {
    return { state: 'ok', worst: null, count: 0, detail: 'auth=ok' };
  }
  const outageInWindow = observations.some((o) => o.kind === 'exchange-unavailable');
  const dead =
    observations.find((o) => !o.selfRecovering && !isCollateralOf(o, outageInWindow)) ?? null;
  if (dead) {
    return {
      state: 'dead',
      worst: dead,
      count: observations.length,
      detail: `auth=dead kind=${dead.kind} n=${observations.length}`,
    };
  }
  // Only self-recovering evidence survived. If a host-key 401 was demoted to
  // get here, say so in the tag rather than reporting a plain outage — the
  // operator should know a 401 was seen and why it is not being called a
  // verdict.
  const demoted = observations.some((o) => !o.selfRecovering);
  if (demoted) {
    const outage = observations.find((o) => o.kind === 'exchange-unavailable')!;
    return {
      state: 'degraded',
      worst: outage,
      count: observations.length,
      detail: `auth=degraded kind=exchange-unavailable n=${observations.length} 401s-demoted`,
    };
  }
  const first = observations[0]!;
  return {
    state: 'degraded',
    worst: first,
    count: observations.length,
    detail: `auth=degraded kind=${first.kind} n=${observations.length}`,
  };
}

/**
 * Pull recognised auth failures out of one transcript's JSONL content.
 *
 * Pure so the parsing is testable without a filesystem — same split as
 * `classifyTranscriptRateLimit` in `agent-serving-probe.ts`, which this
 * deliberately mirrors rather than inventing a second transcript-walking
 * shape.
 *
 * THE TRANSCRIPT, NOT THE PANE. Measured on a live host before writing this:
 * a failing tool's text reaches the JSONL `tool_result` block in full, and
 * does NOT reach `pane.log` — the TUI collapses it to `Error: API Error`. The
 * first version of this was a pane scraper and it would never have matched.
 *
 * @param sinceMs Only entries at or after this epoch ms count. An entry with
 *                an unparseable or missing timestamp is SKIPPED, not counted:
 *                an undateable 401 could be from six weeks ago, and reporting
 *                a stale credential failure as current is the same class of
 *                wrong this whole ticket is about.
 */
export function collectAuthFailuresFromTranscript(
  content: string,
  sinceMs: number,
): AuthFailureObservation[] {
  const out: AuthFailureObservation[] = [];
  if (!content) return out;

  for (const line of content.split('\n')) {
    if (!line) continue;
    // Cheap pre-filter. Parsing every line of a multi-megabyte transcript to
    // find the handful that are errors is the difference between a scan that
    // fits in a 30s poll and one that does not.
    if (!line.includes('tool_result')) continue;

    let entry: unknown;
    try {
      entry = JSON.parse(line);
    } catch {
      continue;
    }
    if (typeof entry !== 'object' || entry === null) continue;
    const rec = entry as Record<string, unknown>;

    const ts = typeof rec['timestamp'] === 'string' ? Date.parse(rec['timestamp']) : NaN;
    if (!Number.isFinite(ts) || ts < sinceMs) continue;

    const message = rec['message'];
    if (typeof message !== 'object' || message === null) continue;
    const blocks = (message as Record<string, unknown>)['content'];
    if (!Array.isArray(blocks)) continue;

    for (const block of blocks) {
      if (typeof block !== 'object' || block === null) continue;
      const b = block as Record<string, unknown>;
      if (b['type'] !== 'tool_result') continue;
      // Only errored results carry a failure to classify. An `is_error` that
      // is absent means success (the API's default); an explicit null means
      // "not determined" — neither is evidence of an auth failure, and
      // treating the null as one would invent a verdict the producer refused
      // to give. Same reading of the field as tool-call-extractor.ts:405-417.
      if (b['is_error'] !== true) continue;

      const failure = classifyPlatformAuthFailure(flattenResultText(b['content']));
      if (!failure) continue;
      out.push({ ...failure, toolName: readToolName(b) });
    }
  }
  return out;
}

/**
 * A `tool_result`'s content is either a string or the block-array form
 * (`[{type:'text',text:'…'}]`). Both shapes appear in real transcripts, so
 * handle both rather than matching only the one that happened to be in the
 * first file I opened.
 */
function flattenResultText(content: unknown): string | null {
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return null;
  const parts: string[] = [];
  for (const item of content) {
    if (typeof item === 'string') parts.push(item);
    else if (typeof item === 'object' && item !== null) {
      const t = (item as Record<string, unknown>)['text'];
      if (typeof t === 'string') parts.push(t);
    }
  }
  return parts.length > 0 ? parts.join('\n') : null;
}

/** Best-effort tool name; the block does not always carry one. */
function readToolName(block: Record<string, unknown>): string | null {
  const n = block['name'] ?? block['tool_name'];
  return typeof n === 'string' && n.length > 0 ? n : null;
}

/**
 * How far back a poll looks for auth failures.
 *
 * Two poll intervals (~30s each) plus slack. Long enough that a failure cannot
 * fall between ticks, short enough that a credential problem fixed ten minutes
 * ago stops being reported as current. The window is the whole reason this
 * reports a STATE rather than a count: "this agent has ever seen a 401" is
 * true of almost every agent and means nothing.
 */
export const AUTH_PROBE_WINDOW_MS = 5 * 60_000;

/**
 * How much of each transcript to read.
 *
 * This runs for every agent on every ~30s poll, and a long-lived transcript is
 * tens of megabytes. Reading them whole would make a health probe the most
 * expensive thing in the tick — the kind of cost that gets a useful signal
 * switched off six months later by someone chasing manager latency.
 *
 * Only the last few minutes matter (see AUTH_PROBE_WINDOW_MS), and the tail is
 * where they are. A tail read can slice mid-line; the parser drops
 * unparseable lines already, so the worst case is losing the one straddling
 * entry, which the next poll's window still covers.
 */
export const MAX_TRANSCRIPT_TAIL_BYTES = 512 * 1024;

/** Read at most `maxBytes` from the end of a file, as UTF-8. */
function readTail(path: string, maxBytes: number): string {
  const size = statSync(path).size;
  const start = size > maxBytes ? size - maxBytes : 0;
  const length = size - start;
  if (length <= 0) return '';
  const buf = Buffer.allocUnsafe(length);
  const fd = openSync(path, 'r');
  try {
    const read = readSync(fd, buf, 0, length, start);
    return buf.subarray(0, read).toString('utf-8');
  } finally {
    closeSync(fd);
  }
}

/**
 * Observe one agent's platform-auth state from its own transcripts.
 *
 * Mirrors `probeRateLimit` (agent-serving-probe.ts) deliberately: same
 * directory resolution, same mtime pre-filter, same injectable `transcriptDir`
 * seam for tests. Returns `ok` on any filesystem trouble — an unreadable
 * transcript is an absence of evidence, and this must never manufacture a
 * credential verdict out of a missing file.
 */
export function probeSessionAuth(args: {
  projectDir: string;
  now?: Date;
  windowMs?: number;
  /** Test seam: override the transcript directory. */
  transcriptDir?: string;
  /** Test seam: shrink the tail so the truncation behaviour is drivable. */
  maxBytesPerFile?: number;
}): SessionAuthVerdict {
  const now = args.now ?? new Date();
  const sinceMs = now.getTime() - (args.windowMs ?? AUTH_PROBE_WINDOW_MS);
  const dir = args.transcriptDir ?? sessionTranscriptDir(args.projectDir);

  let entries: string[];
  try {
    entries = readdirSync(dir);
  } catch {
    return decideSessionAuthState([]);
  }

  const maxBytes = args.maxBytesPerFile ?? MAX_TRANSCRIPT_TAIL_BYTES;
  const observations: AuthFailureObservation[] = [];
  for (const path of transcriptFilesIn(dir)) {
    try {
      const st = statSync(path);
      // A file untouched since before the window opened cannot hold an
      // in-window failure. Cheap upper bound, same trick as probeRateLimit.
      if (!st.isFile() || st.mtimeMs < sinceMs) continue;
    } catch {
      continue;
    }
    let content: string;
    try {
      content = readTail(path, maxBytes);
    } catch {
      continue;
    }
    observations.push(...collectAuthFailuresFromTranscript(content, sinceMs));
  }

  return decideSessionAuthState(observations);
}

/**
 * Every transcript that belongs to this project: the per-session JSONL files,
 * plus each session's sub-agent sidecars.
 *
 * Claude Code writes a delegated worker's transcript to
 * `<transcriptDir>/<sessionId>/subagents/agent-<id>.jsonl` (ENG-6294 relies on
 * the same layout). The first version of this walk read only the top level,
 * which would have missed auth failures in exactly the place a lot of tool
 * calls happen — this session dispatches most of its heavy work to sub-agents,
 * and their calls carry the same platform token. CodeRabbit caught it.
 *
 * Directory shapes we do not recognise are skipped rather than guessed at.
 */
function transcriptFilesIn(dir: string): string[] {
  const out: string[] = [];
  let entries: string[];
  try {
    entries = readdirSync(dir);
  } catch {
    return out;
  }
  for (const name of entries) {
    if (name.endsWith('.jsonl')) {
      out.push(join(dir, name));
      continue;
    }
    // A session directory. Its sub-agent sidecars live one level down.
    const subagentDir = join(dir, name, 'subagents');
    let subs: string[];
    try {
      subs = readdirSync(subagentDir);
    } catch {
      continue;
    }
    for (const sub of subs) {
      if (sub.endsWith('.jsonl')) out.push(join(subagentDir, sub));
    }
  }
  return out;
}
